import { CheckCircle2, ClipboardList, Eye, FileAudio2, SlidersHorizontal, Wand2 } from "lucide-react";
import type { ChildProfile, ContentNode } from "../../types";

interface ContentStudioProps {
  nodes: ContentNode[];
  onNodeStatus: (nodeId: string, status: ContentNode["status"]) => void;
  selectedChild: ChildProfile;
}

export function ContentStudio({ nodes, onNodeStatus, selectedChild }: ContentStudioProps) {
  const selectedNode = nodes.find((node) => node.skill === selectedChild.skills[0]?.id) ?? nodes[0];

  return (
    <div className="studio-layout">
      <section className="surface-panel studio-editor" aria-labelledby="studio-title">
        <div className="panel-heading">
          <div>
            <h3 id="studio-title">Atomic content studio</h3>
            <span>Every object stores skill, supports, success rule, and parent extension.</span>
          </div>
          <button className="secondary-button compact" type="button">
            <Eye size={17} aria-hidden="true" />
            Preview
          </button>
        </div>

        <div className="editor-checklist">
          <ChecklistItem label="Choose learning goal" value={`${selectedNode.subject} - ${selectedNode.strand}`} />
          <ChecklistItem label="Link prerequisite skills" value={selectedNode.prerequisites.join(", ")} />
          <ChecklistItem label="Write explicit teach script" value={selectedNode.instructionScript} />
          <ChecklistItem label="Add guided practice" value={selectedNode.guidedPractice.join(", ")} />
          <ChecklistItem label="Add independent practice" value={selectedNode.independentPractice.join(", ")} />
          <ChecklistItem label="Tag misconceptions" value={selectedNode.misconceptionTags.join(", ")} />
          <ChecklistItem label="Set parent extension" value={selectedNode.parentExtension} />
        </div>

        <div className="studio-actions">
          <button className="secondary-button" onClick={() => onNodeStatus(selectedNode.id, "review")} type="button">
            <ClipboardList size={18} aria-hidden="true" />
            Send to QA
          </button>
          <button className="primary-button" onClick={() => onNodeStatus(selectedNode.id, "ready")} type="button">
            <CheckCircle2 size={18} aria-hidden="true" />
            Mark ready
          </button>
        </div>
      </section>

      <aside className="surface-panel content-health" aria-labelledby="health-title">
        <div className="panel-heading">
          <h3 id="health-title">Content health</h3>
          <SlidersHorizontal size={18} aria-hidden="true" />
        </div>
        <div className="node-list">
          {nodes.map((node) => (
            <article key={node.id}>
              <div>
                <strong>{node.skill}</strong>
                <p>{node.ageBand} - {node.domain}</p>
              </div>
              <span className={`status-pill status-${node.status}`}>{node.status}</span>
            </article>
          ))}
        </div>
        <div className="quality-strip">
          <span className="mini-icon" aria-hidden="true">
            <Wand2 size={18} />
          </span>
          <div>
            <strong>Quality first</strong>
            <p>No monolithic lessons. Cards assemble dynamically for teach, review, and intervention.</p>
          </div>
        </div>
        <div className="audio-strip">
          <FileAudio2 size={18} aria-hidden="true" />
          British English voice QA queue ready for precise phoneme audio.
        </div>
      </aside>
    </div>
  );
}

function ChecklistItem({ label, value }: { label: string; value: string }) {
  return (
    <article>
      <CheckCircle2 size={19} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <p>{value}</p>
      </div>
    </article>
  );
}
